"use client";

import { createContext, useContext, useMemo } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

const InsidePreContext = createContext(false);

interface MarkdownContentProps {
  content: string;
  className?: string;
  showCaret?: boolean;
  fadeOnChangeKey?: number;
  fadeFromIndex?: number;
  fadeRanges?: StreamingTextFadeRange[];
}

export interface StreamingTextFadeRange {
  id: number;
  start: number;
  end: number;
  opacity: number;
  startedAtMs?: number;
}

interface HastNode {
  type: string;
  tagName?: string;
  value?: string;
  properties?: Record<string, unknown>;
  children?: HastNode[];
  position?: {
    start?: { offset?: number };
    end?: { offset?: number };
  };
}

const components: Components = {
  p: ({ children, ...props }) => (
    <p
      className="my-2 text-base leading-6 text-foreground md:text-sm"
      {...props}
    >
      {children}
    </p>
  ),
  strong: ({ children, ...props }) => (
    <strong className="font-semibold text-foreground" {...props}>
      {children}
    </strong>
  ),
  em: ({ children, ...props }) => (
    <em className="italic" {...props}>
      {children}
    </em>
  ),
  del: ({ children, ...props }) => (
    <del className="line-through opacity-70" {...props}>
      {children}
    </del>
  ),
  a: ({ children, href, ...props }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary underline underline-offset-2 hover:text-primary/80"
      {...props}
    >
      {children}
    </a>
  ),
  ul: ({ children, ...props }) => (
    <ul
      className="my-2 list-disc space-y-1 pl-5 text-base text-foreground md:text-sm"
      {...props}
    >
      {children}
    </ul>
  ),
  ol: ({ children, ...props }) => (
    <ol
      className="my-2 list-decimal space-y-1 pl-5 text-base text-foreground md:text-sm"
      {...props}
    >
      {children}
    </ol>
  ),
  li: ({ children, ...props }) => (
    <li className="leading-6" {...props}>
      {children}
    </li>
  ),
  h1: ({ children, ...props }) => (
    <h1
      className="mb-2 mt-4 text-base font-semibold text-foreground first:mt-0"
      {...props}
    >
      {children}
    </h1>
  ),
  h2: ({ children, ...props }) => (
    <h2
      className="mb-2 mt-4 text-base font-semibold text-foreground first:mt-0"
      {...props}
    >
      {children}
    </h2>
  ),
  h3: ({ children, ...props }) => (
    <h3
      className="mb-1 mt-3 text-base font-semibold text-foreground first:mt-0 md:text-sm"
      {...props}
    >
      {children}
    </h3>
  ),
  h4: ({ children, ...props }) => (
    <h4
      className="mb-1 mt-3 text-base font-semibold text-foreground first:mt-0 md:text-sm"
      {...props}
    >
      {children}
    </h4>
  ),
  h5: ({ children, ...props }) => (
    <h5
      className="mb-1 mt-3 text-base font-semibold text-foreground first:mt-0 md:text-sm"
      {...props}
    >
      {children}
    </h5>
  ),
  h6: ({ children, ...props }) => (
    <h6
      className="mb-1 mt-3 text-base font-semibold text-foreground first:mt-0 md:text-sm"
      {...props}
    >
      {children}
    </h6>
  ),
  code: function Code({ className, children, ...props }) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const insidePre = useContext(InsidePreContext);
    return (
      <code
        className={cn(
          "text-xs",
          !insidePre &&
            !className &&
            "rounded bg-muted px-1 py-0.5 text-foreground",
          className
        )}
        {...props}
      >
        {children}
      </code>
    );
  },
  pre: function Pre({ children, ...props }) {
    return (
      <InsidePreContext.Provider value={true}>
        <pre
          className="my-2 max-w-full overflow-x-auto rounded-md bg-muted p-3 text-xs text-foreground"
          {...props}
        >
          {children}
        </pre>
      </InsidePreContext.Provider>
    );
  },
  blockquote: ({ children, ...props }) => (
    <blockquote
      className="my-2 border-l-2 border-border pl-3 text-base italic text-muted-foreground md:text-sm"
      {...props}
    >
      {children}
    </blockquote>
  ),
  hr: ({ ...props }) => <hr className="my-3 border-border" {...props} />,
  table: ({ children, ...props }) => (
    <div
      data-testid="planner-markdown-table-scroll"
      className="my-2 max-w-full overflow-x-auto"
    >
      <Table {...props}>{children}</Table>
    </div>
  ),
  thead: ({ children, ...props }) => (
    <TableHeader className="bg-muted" {...props}>
      {children}
    </TableHeader>
  ),
  tbody: ({ children, ...props }) => (
    <TableBody {...props}>{children}</TableBody>
  ),
  tr: ({ children, ...props }) => <TableRow {...props}>{children}</TableRow>,
  th: ({ children, ...props }) => <TableHead {...props}>{children}</TableHead>,
  td: ({ children, ...props }) => <TableCell {...props}>{children}</TableCell>,
};

export default function MarkdownContent({
  content,
  className,
  showCaret,
  fadeOnChangeKey,
  fadeFromIndex,
  fadeRanges,
}: MarkdownContentProps) {
  const rehypePlugins = useMemo(
    () => [
      createStreamingTextFadePlugin({
        fadeRanges,
        fadeFromIndex,
        fadeClassName:
          fadeOnChangeKey === undefined
            ? undefined
            : fadeOnChangeKey % 2 === 0
              ? "planner-streaming-text-fade-a"
              : "planner-streaming-text-fade-b",
      }),
    ],
    [fadeRanges, fadeFromIndex, fadeOnChangeKey]
  );

  return (
    <div
      className={cn(
        "min-w-0 max-w-full overflow-hidden [&>:first-child]:mt-0 [&>:last-child]:mb-0",
        "text-base text-foreground md:text-sm",
        showCaret && "[&>*:nth-last-child(2)]:inline",
        className
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={rehypePlugins}
        components={components}
      >
        {content}
      </ReactMarkdown>
      {showCaret ? (
        <span className="ml-0.5 inline-block h-4 w-0.5 animate-pulse bg-foreground align-text-bottom" />
      ) : null}
    </div>
  );
}

function createStreamingTextFadePlugin({
  fadeRanges,
  fadeFromIndex,
  fadeClassName,
}: {
  fadeRanges?: StreamingTextFadeRange[];
  fadeFromIndex?: number;
  fadeClassName?: string;
}) {
  return () => (tree: HastNode) => {
    const normalizedFadeRanges =
      fadeRanges && fadeRanges.length > 0
        ? fadeRanges
        : fadeFromIndex !== undefined && fadeClassName
          ? [
              {
                id: 1,
                start: fadeFromIndex,
                end: Number.POSITIVE_INFINITY,
                opacity: 0.22,
              },
            ]
          : [];
    const sortedFadeRanges = [...normalizedFadeRanges]
      .filter((range) => range.end > range.start)
      .sort((a, b) => a.start - b.start);
    if (sortedFadeRanges.length === 0) return;

    let visibleTextIndex = 0;

    const walkChildren = (node: HastNode) => {
      if (!Array.isArray(node.children)) return;

      node.children = node.children.flatMap((child) => {
        if (child.type === "text") {
          const value = child.value ?? "";
          const sourceStart = child.position?.start?.offset;
          const sourceEnd = child.position?.end?.offset;
          const hasSourceOffsets =
            typeof sourceStart === "number" &&
            typeof sourceEnd === "number" &&
            sourceEnd >= sourceStart;
          const start = hasSourceOffsets ? sourceStart : visibleTextIndex;
          const end = hasSourceOffsets ? sourceEnd : start + value.length;
          if (!hasSourceOffsets) {
            visibleTextIndex = end;
          }

          const overlappingRanges = sortedFadeRanges.filter(
            (range) => range.start < end && range.end > start
          );
          if (overlappingRanges.length === 0 || value.length === 0) {
            return [child];
          }

          const segments: HastNode[] = [];
          let localCursor = 0;
          overlappingRanges.forEach((range) => {
            const localStart = Math.max(0, range.start - start);
            const localEnd = Math.min(value.length, range.end - start);
            if (localStart > localCursor) {
              segments.push({
                ...child,
                value: value.slice(localCursor, localStart),
              });
            }
            if (localEnd > localStart) {
              segments.push(
                createFadeNode(value.slice(localStart, localEnd), range.opacity)
              );
            }
            localCursor = Math.max(localCursor, localEnd);
          });
          if (localCursor < value.length) {
            segments.push({ ...child, value: value.slice(localCursor) });
          }
          return segments;
        }

        walkChildren(child);
        return [child];
      });
    };

    walkChildren(tree);
  };
}

function createFadeNode(value: string, opacity: number): HastNode {
  return {
    type: "element",
    tagName: "span",
    properties: {
      className: ["planner-streaming-content-fade"],
      style: { opacity },
    },
    children: [{ type: "text", value }],
  };
}
